// The Tiendas list (/tiendas) as data: its URL filters, the row model and
// the link builder the overview tiles use. Mechanics in ./listState.ts.

import type { FacilityDashboardAsset, FacilityDashboardAssetGroup } from "@bitacorapro/api-types";
import { daysSince, deriveStoreStatus, normalizeSearch, type StoreStatus } from "../util";
import {
  applyFilters as applyListFilters,
  clearFilters,
  parseListState as parseList,
  writeListState as writeList,
  type ApplyOptions,
  type ListConfig,
  type ListPatch,
  type ListState,
} from "./listState";

/** «Con reporte en los últimos 30 días» — the same window as the overview's
 *  coverage tile, which links here. */
export const COBERTURA_DIAS = 30;

export type Reporte = "hasta30" | "mas30";
export const REPORTES: readonly Reporte[] = ["hasta30", "mas30"];
export const REPORTE_LABEL: Record<Reporte, string> = {
  hasta30: "Con reporte en los últimos 30 días",
  mas30: "Sin reporte en los últimos 30 días",
};

/** The Estado chip's domain. `crit` stays in StoreStatus for its other
 *  consumers, but deriveStoreStatus never yields it (founder decision
 *  2026-10-22), so it is not an option. */
export const ESTADOS_TIENDA: readonly StoreStatus[] = ["ok", "warn", "idle"];

export type FilterKey = "zona" | "estado" | "reporte" | "marca";
export const FILTER_KEYS: readonly FilterKey[] = ["zona", "estado", "reporte", "marca"];

export const LIST_CONFIG: ListConfig<FilterKey> = {
  keys: FILTER_KEYS,
  domains: { estado: ESTADOS_TIENDA, reporte: REPORTES },
};

export type TiendasState = ListState<FilterKey>;
export type TiendasPatch = ListPatch<FilterKey>;

export const CLEAR_FILTERS: TiendasPatch = clearFilters(FILTER_KEYS);

/** hasta30 = a report at most 30 days ago; mas30 = older, or never. */
export function reporteOf(lastVisit: string | null | undefined, now: Date | number): Reporte {
  const d = daysSince(lastVisit, typeof now === "number" ? new Date(now) : now);
  return d !== null && d <= COBERTURA_DIAS ? "hasta30" : "mas30";
}

export type EnrichedAsset = FacilityDashboardAsset & { status: StoreStatus };

export type FilaTienda = {
  id: string;
  t: EnrichedAsset;
  zonaId: string;
  zona: string | null;
  reporte: Reporte;
  haystack: string;
};

export function buildFilas(
  byAsset: readonly FacilityDashboardAsset[],
  byAssetGroup: readonly FacilityDashboardAssetGroup[],
  idleAfterDays: number | undefined,
  now: Date | number
): FilaTienda[] {
  const zoneNameById = new Map(byAssetGroup.map((g) => [g.id, g.name]));
  return byAsset.map((a) => {
    const t: EnrichedAsset = {
      ...a,
      status: deriveStoreStatus({
        openFindingsCount: a.openFindingsCount,
        lastVisit: a.lastVisit,
        idleAfterDays,
      }),
    };
    const zona = a.assetGroupId ? (zoneNameById.get(a.assetGroupId) ?? null) : null;
    return {
      id: a.id,
      t,
      zonaId: a.assetGroupId ?? "",
      zona,
      reporte: reporteOf(a.lastVisit, now),
      haystack: normalizeSearch([a.address, a.unit, a.brandName, zona].filter(Boolean).join(" ")),
    };
  });
}

export function valueOf(f: FilaTienda, k: FilterKey): string {
  switch (k) {
    case "zona":
      return f.zonaId;
    case "estado":
      return f.t.status;
    case "reporte":
      return f.reporte;
    case "marca":
      return f.t.brandId ?? "";
  }
}

export function parseListState(sp: URLSearchParams): TiendasState {
  return parseList(sp, LIST_CONFIG);
}

export function writeListState(prev: URLSearchParams, patch: TiendasPatch): URLSearchParams {
  return writeList(prev, patch, LIST_CONFIG);
}

export function applyFilters(
  filas: readonly FilaTienda[],
  s: TiendasState,
  opts: ApplyOptions<FilterKey> = {}
): FilaTienda[] {
  return applyListFilters(filas, s, FILTER_KEYS, valueOf, opts);
}

/** `/tiendas` with this filter set, canonical (built through
 *  writeListState, never a literal). */
export function tiendasHref(patch: TiendasPatch): string {
  const qs = writeListState(new URLSearchParams(), patch).toString();
  return qs ? `/tiendas?${qs}` : "/tiendas";
}
